import React, { FunctionComponent, useState } from 'react';
import { ButtonBase, Menu, MenuItem, SxProps, Tooltip } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import moment from 'moment';

import { ISection } from '../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  ZOOM_MEETING_REQUEST_STATUSES,
  ZOOM_MEETING_REQUEST_TYPES,
} from '../../../../store/V2/ZoomRequestsSlice';
import { checkUserPermission } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/UserSlice';

interface ZoomMeetingOptionsButtonWithMenuProps {
  section: ISection;
  isOneToOne: boolean;
  onCancelClass: (section: ISection) => void;
  onRescheduleClass: (section: ISection) => void;
}

const MenuDividerLine = (
  <div
    style={{
      height: '1px',
      width: '100%',
      borderBottom: '1px dashed #CBD5E1',
      margin: '20px 0',
    }}
  />
);

const classOptionsDropDownButtonStyles: SxProps = {
  backgroundColor: '#E2E8F0',
  color: '#000000',
  borderRadius: '5px',
  padding: '5px',
};

const classOptionsMenuStyles: SxProps = {
  fontSize: '14px',
  fontWeight: 600,
  p: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

const ZoomMeetingOptionsButtonWithMenu: FunctionComponent<ZoomMeetingOptionsButtonWithMenuProps> = ({
  section,
  isOneToOne,
  onCancelClass,
  onRescheduleClass,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const authUser = authState.user;
  const classOptionsMenuId = `${section.eventId}-class-options-menu`;
  const classOptionsMenuButtonId = `${section.eventId}-class-options-menu-button`;
  const isClassOptionsMenuOpen = Boolean(anchorEl);
  const isExpired = moment(section.dateTime) < moment();
  const isZoomRequestCanceled =
    section.zoomRequest &&
    section.zoomRequest.type === ZOOM_MEETING_REQUEST_TYPES.CANCEL &&
    section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.APPROVED;

  const isRequestPending =
    section.zoomRequest &&
    section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING;

  const classCancellationPermission =
    isOneToOne &&
    !isZoomRequestCanceled &&
    !isRequestPending &&
    !isExpired &&
    checkUserPermission(authUser, {
      schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT],
    });

  const classReschedulePermission =
    isOneToOne &&
    !isZoomRequestCanceled &&
    !isRequestPending &&
    !isExpired &&
    checkUserPermission(authUser, {
      schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT],
    });

  const isClassOptionsDropDownVisible =
    classCancellationPermission || classReschedulePermission;

  const openClassOptionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeClassOptionsMenu = () => {
    setAnchorEl(null);
  };

  const handleCancelClass = () => {
    onCancelClass(section);
    closeClassOptionsMenu();
  };

  const handleRescheduleClass = () => {
    onRescheduleClass(section);
    closeClassOptionsMenu();
  };

  if (!isClassOptionsDropDownVisible) {
    return null;
  }

  return (
    <>
      <Tooltip title="Rescheduling & Cancellation" placement="top">
        <ButtonBase
          aria-haspopup="true"
          id={classOptionsMenuButtonId}
          sx={classOptionsDropDownButtonStyles}
          onClick={openClassOptionsMenu}
          aria-controls={
            isClassOptionsMenuOpen ? classOptionsMenuId : undefined
          }
          aria-expanded={isClassOptionsMenuOpen ? 'true' : undefined}
        >
          <MoreHorizIcon />
        </ButtonBase>
      </Tooltip>
      <Menu
        id={classOptionsMenuId}
        anchorEl={anchorEl}
        open={isClassOptionsMenuOpen}
        onClose={closeClassOptionsMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: '10px',
              padding: '20px',
              border: '1px solid #CBD5E1',
            },
          },
        }}
        MenuListProps={{
          'aria-labelledby': classOptionsMenuButtonId,
          sx: { p: 0 },
        }}
      >
        {classReschedulePermission ? (
          <MenuItem
            onClick={handleRescheduleClass}
            sx={{ ...classOptionsMenuStyles }}
          >
            Reschedule Class
          </MenuItem>
        ) : null}
        {MenuDividerLine}
        {classCancellationPermission ? (
          <MenuItem
            onClick={handleCancelClass}
            sx={{ ...classOptionsMenuStyles, color: 'error.main' }}
          >
            Cancel Class
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default ZoomMeetingOptionsButtonWithMenu;
